import { timeFrame } from 'HelperFunctions/Time';
import converter from 'json-style-converter/es5';
import { LoadList, LoadListEntry, RentalBundle } from '../types/graphql';

export const pickupAndDeliveryAddresses = (rentalTransport) => {
  let deliveryAddressCity,
    deliveryAddressLocale,
    deliveryAddressPostalCode,
    deliveryAddressLocationName,
    deliveryAddressStreetAddress1,
    deliveryAddressStreetAddress2,
    deliveryAddressCountry,
    pickupAddressCity,
    pickupAddressLocale,
    pickupAddressLocationName,
    pickupAddressPostalCode,
    pickupAddressStreetAddress1,
    pickupAddressStreetAddress2,
    pickupAddressCountry;

  if (rentalTransport.serviceTicket) {
    const serviceTicket = converter.snakeToCamelCase(
      rentalTransport.serviceTicket,
    );

    pickupAddressCity = deliveryAddressCity = serviceTicket.city;
    pickupAddressLocale = deliveryAddressLocale = serviceTicket.locale;
    pickupAddressLocationName = deliveryAddressPostalCode =
      serviceTicket.postalCode;
    pickupAddressPostalCode = deliveryAddressLocationName =
      serviceTicket.locationName;
    pickupAddressStreetAddress1 = deliveryAddressStreetAddress1 =
      serviceTicket.streetAddress1;
    pickupAddressStreetAddress2 = deliveryAddressStreetAddress2 =
      serviceTicket.streetAddress2;
    pickupAddressCountry = deliveryAddressCountry = serviceTicket.country;
  } else if (rentalTransport.rentalStaff?.rentalStaffRecurrence) {
    const rentalStaffRecurrence = converter.snakeToCamelCase(
      rentalTransport.rentalStaff.rentalStaffRecurrence,
    );

    pickupAddressCity = deliveryAddressCity = rentalStaffRecurrence.city;
    pickupAddressLocale = deliveryAddressLocale = rentalStaffRecurrence.locale;
    pickupAddressLocationName = deliveryAddressPostalCode =
      rentalStaffRecurrence.postalCode;
    pickupAddressPostalCode = deliveryAddressLocationName =
      rentalStaffRecurrence.locationName;
    pickupAddressStreetAddress1 = deliveryAddressStreetAddress1 =
      rentalStaffRecurrence.streetAddress1;
    pickupAddressStreetAddress2 = deliveryAddressStreetAddress2 =
      rentalStaffRecurrence.streetAddress2;
    pickupAddressCountry = deliveryAddressCountry =
      rentalStaffRecurrence.country;
  } else {
    const rental = rentalTransport.rental;

    pickupAddressCity = deliveryAddressCity = rental.deliveryAddressCity;
    pickupAddressLocale = deliveryAddressLocale = rental.deliveryAddressLocale;
    pickupAddressLocationName = deliveryAddressPostalCode =
      rental.deliveryAddressPostalCode;
    pickupAddressPostalCode = deliveryAddressLocationName =
      rental.deliveryAddressLocationName;
    pickupAddressStreetAddress1 = deliveryAddressStreetAddress1 =
      rental.deliveryAddressStreetAddress1;
    pickupAddressStreetAddress2 = deliveryAddressStreetAddress2 =
      rental.deliveryAddressStreetAddress2;
    pickupAddressCountry = deliveryAddressCountry =
      rental.deliveryAddressCountry;
  }

  return {
    deliveryAddressCity,
    deliveryAddressLocale,
    deliveryAddressPostalCode,
    deliveryAddressLocationName,
    deliveryAddressStreetAddress1,
    deliveryAddressStreetAddress2,
    deliveryAddressCountry,
    pickupAddressCity,
    pickupAddressLocale,
    pickupAddressLocationName,
    pickupAddressPostalCode,
    pickupAddressStreetAddress1,
    pickupAddressStreetAddress2,
    pickupAddressCountry,
  };
};

export const routingTimeLabel = (rentalTransport, deliverySetting) => {
  if (!rentalTransport.serviceTicketId && !rentalTransport.rentalStaffId)
    return '';

  const rentalStaffRecurrence = converter.snakeToCamelCase(
    (rentalTransport.serviceTicket?.rentalStaff || rentalTransport.rentalStaff)
      ?.rentalStaffRecurrence || {},
  );

  if (!rentalStaffRecurrence || !rentalStaffRecurrence.requiresRouting)
    return '';

  const {
    morningStartTime,
    morningEndTime,
    afternoonStartTime,
    afternoonEndTime,
    eveningStartTime,
    eveningEndTime,
  } = deliverySetting;

  let label = '';

  const routingTime =
    rentalTransport.serviceTicketId && rentalTransport.serviceTicket
      ? rentalTransport.serviceTicket.routingTime
      : rentalStaffRecurrence.routingTime;

  if (['morning', 'afternoon', 'night'].includes(routingTime)) {
    label =
      routingTime == 'morning'
        ? timeFrame(morningStartTime, morningEndTime)
        : routingTime == 'afternoon'
          ? timeFrame(afternoonStartTime, afternoonEndTime)
          : timeFrame(eveningStartTime, eveningEndTime);
  }

  return label;
};

export const getItemListFromRentalTransport = (rentalTransport) => {
  const { rental } = rentalTransport;
  return {
    items: rental?.rentalItems ?? [],
    accessories: rental?.rentalAccessories ?? [],
    addOns: rental?.rentalAddOns ?? [],
    bundles: rental?.rentalBundles ?? [],
  };
};

export const getItemListFromLoadList = (
  loadList: LoadList,
  rentalBundles?: Pick<RentalBundle, 'id' | 'rentalItems' | 'rentalAccessories' | 'rentalAddOns'>[],
) => {
  if (!loadList) {
    return {
      items: [],
      accessories: [],
      addOns: [],
      bundles: [],
    };
  }

  const filterByTypes = (loadListEntries: LoadListEntry[] | null | undefined, types: string[]) =>
    loadListEntries?.filter((entry) =>
      types.includes(entry.rentalInventoryType),
    ) || [];

  // NOTE: inventoryName and quantity are properties from the REST endpoint.
  // The GraphQL loadListEntries fragment does not include these fields yet.
  // This will need to be revisited when the routing feature is refactored to use GraphQL.
  const filterAttributes = (entry) => ({
    name: entry.inventoryName,
    quantity: entry.quantity,
  });

  return {
    items: filterByTypes(loadList?.loadListEntries, ['RentalItem']).map(
      filterAttributes,
    ),
    accessories: filterByTypes(loadList?.loadListEntries, [
      'RentalAccessory',
    ]).map(filterAttributes),
    addOns: filterByTypes(loadList?.loadListEntries, ['RentalAddOn']).map(
      filterAttributes,
    ),
    bundles: filterByTypes(loadList?.loadListEntries, ['RentalBundle']).map((entry) => {
      const bundleDetails = rentalBundles?.find(
        (b) => String(b.id) === String(entry.rentalBundleId),
      );
      return {
        ...filterAttributes(entry),
        rentalItems: bundleDetails?.rentalItems ?? [],
        rentalAccessories: bundleDetails?.rentalAccessories ?? [],
        rentalAddOns: bundleDetails?.rentalAddOns ?? [],
      };
    }),
  };
};

export interface RouteDate {
  year: string;
  month: string;
  day?: string;
}

export interface ProcessedRouteDate {
  year: number;
  month: number;
  day: number;
  startDate: Date;
  endDate: Date;
}

export const processRouteDate = (routeDate: RouteDate): ProcessedRouteDate => {
  const year = routeDate.year;
  const month = routeDate.month;
  const day = routeDate.day;

  // @ts-ignore
  const ret : ProcessedRouteDate = {};

  if (!isNaN(parseInt(year)) && !isNaN(parseInt(month))) {
    ret.year = parseInt(year);
    ret.month = parseInt(month);

    if (!isNaN(parseInt(day))) {
      ret.day = parseInt(day);
      ret.startDate = new Date(ret.year, ret.month - 1, ret.day);
      ret.endDate = new Date(ret.startDate);
      ret.endDate.setHours(23, 59, 59);
    } else {
      ret.day = 1;
      ret.startDate = new Date(ret.year, ret.month - 1, 1);
      ret.endDate = new Date(ret.year, ret.month, 0);
      ret.endDate.setHours(23, 59, 59);
    }
  }
  return ret;
};
export const getUrlForDate = (inputDate: any, path: string) => {
  let date = new Date(inputDate);
  date = !isNaN(date) ? date : new Date();
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // JavaScript months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');

  let url = path;
  url = url.replace(/:year[^\/]*/, year);
  url = url.replace(/:month[^\/]*/, month);
  url = url.replace(/:day[^\/]*/, day);
  return url;
};
export const getRoutingCalendarUrl = (inputDate: any) => {
  return getUrlForDate(inputDate, `/operations/routing/calendar/:year/:month`);
};
export const getRoutingMapUrl = (inputDate: any) => {
  return getUrlForDate(inputDate, `/operations/routing/map/:year/:month/:day`);
};
export const getRoutingOrdersUrl = (inputDate: any) => {
  return getUrlForDate(
    inputDate,
    `/operations/routing/orders/:year/:month/:day`,
  );
};

export const getRoutingTruckRouteUrl = (truckRouteId: string | number) => {
  return `/operations/routing/map/routes/${truckRouteId}`;
};
